<svelte:options
	customElement={{
		tag: 'rf-light-table-gui',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module lang="ts">
	export interface ReframeLightTableGuiProps {
		x?: number;
		y?: number;
		rotation?: number;
		width?: number;
		height?: number;
		order?: number;
		opacity?: number;
		totalElements?: number;
		selectedElementId?: string | null;
		interactive?: boolean;
		inInspector?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Imports
	 */
	import { createEventDispatcher } from 'svelte';

	const dispatch = createEventDispatcher();

	/**
	 * Props
	 */
	const {
		x = 0,
		y = 0,
		rotation = 0,
		width = 0,
		height = 0,
		order = 0,
		opacity = 1,
		totalElements = 0,
		selectedElementId = null,
		interactive = false,
		inInspector = false,
	}: ReframeLightTableGuiProps = $props();

	/**
	 * State
	 */
	const editableProps = $state({
		rotation,
		opacity,
		order,
	});
	let hasCopiedValues = $state(false);
	let previousProps = $state({ rotation, opacity, order });

	/**
	 * Update editableProps when props change
	 */
	$effect(() => {
		// Only update if the props have actually changed
		if (previousProps.rotation !== rotation || previousProps.opacity !== opacity || previousProps.order !== order) {
			editableProps.rotation = rotation;
			editableProps.opacity = opacity;
			editableProps.order = order;

			// Update previous props
			previousProps = { rotation, opacity, order };
		}
	});

	/**
	 * Methods
	 */
	const handlePropertyChange = (event: Event, property: 'rotation' | 'opacity') => {
		const target = event.target as HTMLInputElement;
		const value = parseFloat(target.value);
		editableProps[property] = value;
		dispatch(`${property}-change`, value);
	};

	const handleResetSlider = (_: Event, property: 'rotation' | 'opacity', value: number = 0) => {
		editableProps[property] = value;
		dispatch(`${property}-change`, editableProps[property]);
	};

	const handleOrderChange = (_: Event, value: number) => {
		editableProps.order = value;
		dispatch('order-change', editableProps.order);
	};

	const copyValuesToClipboard = () => {
		navigator.clipboard.writeText(
			`x="${x.toFixed(2)}" y="${y.toFixed(2)}" rotation="${rotation}" opacity="${opacity}" order="${order}" width="${width}" height="${height}"`
		);
		hasCopiedValues = true;
		setTimeout(() => {
			hasCopiedValues = false;
		}, 2000);
	};
</script>

<div class="rf-light-table-element-gui">
	<div class="rf-light-table-element-gui__header">
		<h6>rf-light-table gui</h6>
	</div>
	<div class="rf-light-table-element-gui__info">
		<div class="rf-light-table-element-gui__info__drawer">
			<h6>
				current element: <br />
				{selectedElementId}
			</h6>
		</div>
		<div class="rf-light-table-element-gui__info__read-only">
			<p>X: {x.toFixed(2)}</p>
			<p>Y: {y.toFixed(2)}</p>
			<p>Width: {(width * 100).toFixed(2)}%</p>
			<p>Height: {(height * 100).toFixed(2)}%</p>
		</div>
		<div class="rf-light-table-element-gui__info__drawer">
			{#if !interactive}
				<p>Rotation: {`${editableProps.rotation}°`}</p>
				<input
					type="range"
					min="-360"
					max="360"
					value={editableProps.rotation || 0}
					oninput={(e) => handlePropertyChange(e, 'rotation')}
					ondblclick={(e) => handleResetSlider(e, 'rotation')}
				/>
			{/if}
			{#if interactive}
				<p>⚠️ Interactive elements cannot be rotated</p>
			{/if}
		</div>
		<div class="rf-light-table-element-gui__info__drawer">
			<p>Opacity: {(editableProps.opacity * 100).toFixed(2)}%</p>
			<input
				type="range"
				min="0"
				max="1"
				step="0.01"
				value={editableProps.opacity || 1}
				oninput={(e) => handlePropertyChange(e, 'opacity')}
				ondblclick={(e) => handleResetSlider(e, 'opacity', 1)}
			/>
		</div>
		<div class="rf-light-table-element-gui__info__drawer">
			<p>Order {editableProps.order} of {totalElements}</p>
			<div class="rf-light-table-element-gui__info__drawer__buttons">
				<button class="rf-light-table-element-gui__info__drawer__button" onclick={(e) => handleOrderChange(e, totalElements)}>
					Bring to front
				</button>
				<button class="rf-light-table-element-gui__info__drawer__button" onclick={(e) => handleOrderChange(e, 1)}>Send to back</button>
			</div>
		</div>
	</div>
	{#if !inInspector}
		<button class="rf-light-table-element-gui__copy" onclick={copyValuesToClipboard}>
			{hasCopiedValues ? 'Copied!' : 'Copy values to clipboard'}
		</button>
	{/if}
</div>

<style lang="scss" global>
	.rf-light-table-element-gui {
		position: fixed;
		box-sizing: border-box;
		top: var(--rf-spacing-s, 8px);
		right: var(--rf-spacing-s, 8px);
		width: 200px;
		z-index: 1000;
		background-color: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(10px);
		border-radius: 4px;
		color: white;
		pointer-events: auto;
		overflow: hidden;
		&__header {
			box-sizing: border-box;
			width: 100%;
			padding: var(--rf-spacing-xs, 4px);
			background-color: rgba(0, 0, 0, 0.5);

			h6 {
				margin: 0;
				text-transform: uppercase;
				font-family: monospace;
				text-align: center;
			}
		}
		&__info {
			padding: var(--rf-spacing-s, 8px);
			&__read-only,
			&__drawer {
				padding-block: var(--rf-spacing-s, 8px);
				border-bottom: 1px solid rgba(255, 255, 255, 0.1);
				&:first-child {
					padding-top: 0;
				}
				&:last-child {
					border-bottom: none;
					padding-bottom: 0;
				}
				h6,
				p {
					margin: var(--rf-spacing-xs, 4px) 0;
					font-family: monospace;
				}
			}

			&__drawer__buttons {
				display: flex;
				gap: var(--rf-spacing-xs, 4px);
				button {
					flex: 1;
					background-color: rgba(255, 255, 255, 1);
					border: none;
					color: black;
					cursor: pointer;
					padding: var(--rf-spacing-xs, 4px);
					&:hover {
						background-color: rgba(255, 255, 255, 0.7);
					}
				}
			}
			input[type='range'] {
				width: 100%;
			}
		}

		&__copy {
			width: 100%;
			padding: var(--rf-spacing-s, 8px);
			background-color: rgba(0, 0, 0, 0.5);
			border: none;
			color: white;
			cursor: pointer;
			&:hover {
				background-color: rgba(0, 0, 0, 0.7);
			}
		}
	}
</style>
